import { useMemo, useState } from "react";
import GameLauncher from "../shared/GameLauncher";
import GameProgress from "../shared/GameProgress";
import GameResults from "../shared/GameResults";
import GameInterpretation from "../shared/GameInterpretation.jsx";
import { getBasicInterpretation } from "../shared/gameInterpretation";
import "./MissingPieces.css";

const SHAPES = ["🚗", "🏠", "🐱", "🌸", "🚀", "🦋", "🐠", "🦖"];

const GAME_CONFIG = {
  easy: {
    label: "Fácil",
    totalRounds: 8,
    totalPieces: 12,
    minMissing: 2,
    maxMissing: 5,
  },
  medium: {
    label: "Media",
    totalRounds: 10,
    totalPieces: 20,
    minMissing: 4,
    maxMissing: 9,
  },
  hard: {
    label: "Difícil",
    totalRounds: 12,
    totalPieces: 30,
    minMissing: 8,
    maxMissing: 15,
  },
};

function randomNumber(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function randomItem(list) {
  return list[Math.floor(Math.random() * list.length)];
}

function shuffle(list) {
  return [...list].sort(() => Math.random() - 0.5);
}

function createOptions(correct, config) {
  const options = new Set([correct]);

  while (options.size < 4) {
    const variation = randomNumber(-3, 3);
    const option = correct + variation;

    if (
      option >= config.minMissing &&
      option <= config.maxMissing + 4 &&
      option > 0
    ) {
      options.add(option);
    }
  }

  return shuffle([...options]);
}

function createRound(config) {
  const missing = randomNumber(config.minMissing, config.maxMissing);
  const filled = config.totalPieces - missing;

  return {
    shape: randomItem(SHAPES),
    missing,
    filled,
    totalPieces: config.totalPieces,
    options: createOptions(missing, config),
  };
}

export default function MissingPieces() {
  const [selectedDifficulty, setSelectedDifficulty] = useState(null);
  const [config, setConfig] = useState(null);

  const [roundIndex, setRoundIndex] = useState(0);
  const [round, setRound] = useState(null);
  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);

  const [correct, setCorrect] = useState(0);
  const [errors, setErrors] = useState(0);
  const [responses, setResponses] = useState([]);

  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [roundStartTime, setRoundStartTime] = useState(null);

  const totalRounds = config?.totalRounds || 0;

  const progress = useMemo(() => {
    if (!totalRounds) return 0;
    return Math.round((roundIndex / totalRounds) * 100);
  }, [roundIndex, totalRounds]);

  const accuracy =
    totalRounds > 0 ? Math.round((correct / totalRounds) * 100) : 0;

  const averageReactionTime =
    responses.length > 0
      ? Math.round(
          responses.reduce((acc, item) => acc + item.reactionTime, 0) /
            responses.length
        )
      : 0;

  const totalTime =
    startTime && endTime ? Math.round((endTime - startTime) / 1000) : 0;

  const interpretation = finished
    ? getBasicInterpretation({
        accuracy,
        errors,
        averageReactionTime,
        difficulty: selectedDifficulty,
      })
    : null;

  const startGame = (difficulty = selectedDifficulty || "easy") => {
    const nextConfig = GAME_CONFIG[difficulty] || GAME_CONFIG.easy;
    const now = Date.now();

    setSelectedDifficulty(difficulty);
    setConfig(nextConfig);
    setRoundIndex(0);
    setCorrect(0);
    setErrors(0);
    setResponses([]);
    setStarted(true);
    setFinished(false);
    setStartTime(now);
    setEndTime(null);
    setRoundStartTime(now);
    setRound(createRound(nextConfig));
  };

  const answer = (value) => {
    if (!started || finished || !round || !config) return;

    const now = Date.now();
    const reactionTime = now - roundStartTime;
    const isCorrect = value === round.missing;

    setResponses((prev) => [
      ...prev,
      {
        round: roundIndex + 1,
        answer: value,
        correctAnswer: round.missing,
        isCorrect,
        reactionTime,
      },
    ]);

    if (isCorrect) setCorrect((prev) => prev + 1);
    else setErrors((prev) => prev + 1);

    const nextRound = roundIndex + 1;

    if (nextRound >= config.totalRounds) {
      setFinished(true);
      setStarted(false);
      setEndTime(now);
      return;
    }

    setRoundIndex(nextRound);
    setRound(createRound(config));
    setRoundStartTime(now);
  };

  const backToLauncher = () => {
    setSelectedDifficulty(null);
    setConfig(null);
    setStarted(false);
    setFinished(false);
    setRound(null);
  };

  if (!selectedDifficulty && !started && !finished) {
    return (
      <GameLauncher
        title="Piezas perdidas"
        description="Observa la figura y cuenta cuántas piezas faltan por completar."
        areas={[
          "Atención selectiva",
          "Atención sostenida",
          "Escaneo visual",
          "Conteo",
        ]}
        defaultDifficulty="easy"
        onStart={startGame}
      />
    );
  }

  return (
    <div className="missing-pieces-page">
      <div className="missing-pieces-card">
        <header className="missing-pieces-header">
          <div>
            <h1>Piezas perdidas</h1>
            <p>
              Dificultad: <strong>{config?.label}</strong>
            </p>
          </div>

          <button type="button" onClick={() => startGame(selectedDifficulty)}>
            Reiniciar
          </button>
        </header>

        {started && round && (
          <>
            <GameProgress
              label="Ronda"
              current={roundIndex + 1}
              total={totalRounds}
              progress={progress}
            />

            <section className="missing-question">
              <h2>¿Cuántas piezas faltan?</h2>
            </section>

            <section className="missing-stage">
              <div className="missing-shape">{round.shape}</div>

              <div className="pieces-grid">
                {Array.from({ length: round.totalPieces }).map((_, index) => {
                  const isMissing = index >= round.filled;

                  return (
                    <span
                      key={index}
                      className={isMissing ? "piece missing" : "piece filled"}
                    />
                  );
                })}
              </div>
            </section>

            <section className="missing-options">
              {round.options.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => answer(option)}
                >
                  {option}
                </button>
              ))}
            </section>

            <section className="missing-score">
              <span>Aciertos: {correct}</span>
              <span>Errores: {errors}</span>
              <span>Precisión: {accuracy}%</span>
            </section>
          </>
        )}

        {finished && (
          <>
            <GameResults
              results={[
                { label: "Aciertos", value: correct },
                { label: "Errores", value: errors },
                { label: "Precisión", value: `${accuracy}%` },
                { label: "Tiempo medio", value: `${averageReactionTime} ms` },
                { label: "Tiempo total", value: `${totalTime}s` },
              ]}
              onRepeat={() => startGame(selectedDifficulty)}
              onChangeDifficulty={backToLauncher}
            />

            <GameInterpretation interpretation={interpretation} />
          </>
        )}
      </div>
    </div>
  );
}